/**
 * Whitelabel Theme Application Module
 * Handles theme application, DOM manipulation, and error handling
 */

import { getWhitelabelConfig, getAssetUrl } from '../config/whitelabel'
import type { WhitelabelConfig } from '../config/whitelabel'

/**
 * Apply whitelabel theme to the document
 * Includes comprehensive error handling and graceful fallbacks
 */
export function applyWhitelabelTheme(): void {
  try {
    const config = getWhitelabelConfig()
    
    console.log('🎨 Applying whitelabel theme...')
    
    // Apply CSS custom properties
    applyCSSProperties(config)
    
    // Update document title
    updateDocumentTitle(config)
    
    // Update favicon
    updateFavicon(config)
    
    console.log('✅ Whitelabel theme applied successfully')
    
  } catch (error) {
    console.warn('⚠️ Failed to apply whitelabel theme:', error)
    console.warn('🔄 Continuing with default theme...')
    // Application continues with default theme - don't fail the app
  }
}

/**
 * Apply CSS custom properties for theming
 */
function applyCSSProperties(config: WhitelabelConfig): void {
  try {
    const root = document.documentElement
    
    if (!root) {
      throw new Error('Document root element not found')
    }
    
    // Apply primary color
    root.style.setProperty('--ion-color-primary', config.primaryColor)
    root.style.setProperty('--ion-color-primary-rgb', hexToRgb(config.primaryColor))
    
    // Apply secondary color if provided
    if (config.secondaryColor) {
      root.style.setProperty('--ion-color-secondary', config.secondaryColor)
      root.style.setProperty('--ion-color-secondary-rgb', hexToRgb(config.secondaryColor))
    }
    
    console.log('🎨 CSS properties applied:', { 
      primary: config.primaryColor, 
      secondary: config.secondaryColor 
    })
    
  } catch (error) {
    console.warn('Failed to apply CSS properties:', error)
    throw new Error(`CSS application failed: ${error}`)
  }
}

/**
 * Update document title
 */
function updateDocumentTitle(config: WhitelabelConfig): void {
  try {
    if (config.appName && config.appName.trim()) {
      document.title = config.appName
      console.log('📝 Document title updated:', config.appName)
    }
  } catch (error) {
    console.warn('Failed to update document title:', error)
    // Non-critical error, continue with default title
  }
}

/**
 * Update favicon with error handling
 * Simple approach that updates existing favicon link element
 */
function updateFavicon(config: WhitelabelConfig): void {
  if (!config.faviconUrl) {
    console.log('🚫 No custom favicon configured, keeping default')
    return
  }
  
  try {
    updateFaviconLink(config.faviconUrl)
    console.log('🖼️ Favicon updated:', config.faviconUrl)
    
  } catch (error) {
    console.warn('Failed to update favicon:', error)
    // Non-critical error, continue with default favicon
  }
}

/**
 * Simple favicon update function - Subtask 4.2
 * Updates existing favicon link element href attribute
 */
export function updateFaviconLink(faviconUrl: string): void {
  try {
    // Find existing favicon link
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
    
    if (!favicon) {
      // Find alternative favicon links
      favicon = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement
    }
    
    if (!favicon) {
      // Create new favicon link if none exists
      favicon = document.createElement('link')
      favicon.rel = 'icon'
      favicon.type = 'image/x-icon'
      document.head.appendChild(favicon)
      console.log('🆕 Created new favicon link element')
    }
    
    // Get asset URL using whitelabel config helper
    const fullUrl = getAssetUrl(faviconUrl)
    favicon.href = fullUrl
    
    console.log('🔗 Favicon link updated:', fullUrl)
  } catch (error) {
    console.warn('Failed to update favicon link:', error)
    // Graceful degradation - continue without favicon update
  }
}

/**
 * Convert hex color to RGB values for CSS custom properties
 */
function hexToRgb(hex: string): string {
  try {
    // Remove # if present
    hex = hex.replace('#', '')
    
    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    
    return `${r}, ${g}, ${b}`
  } catch (error) {
    console.warn('Failed to convert hex to RGB:', hex, error)
    return '56, 128, 255' // Default blue RGB
  }
}

/**
 * Theme application validation
 * Checks if theme was applied correctly
 */
export function validateThemeApplication(): boolean {
  try {
    const config = getWhitelabelConfig()
    const root = document.documentElement
    
    // Check if primary color was applied
    const appliedPrimary = root.style.getPropertyValue('--ion-color-primary')
    if (appliedPrimary !== config.primaryColor) {
      console.warn('Theme validation failed: Primary color mismatch')
      return false
    }
    
    // Check if document title was updated
    if (config.appName && document.title !== config.appName) {
      console.warn('Theme validation failed: Document title mismatch')
      return false
    }
    
    console.log('✅ Theme application validated successfully')
    return true
    
  } catch (error) {
    console.warn('Theme validation error:', error)
    return false
  }
}

/**
 * Get current theme status for debugging
 */
export function getThemeStatus() {
  try {
    const config = getWhitelabelConfig()
    const root = document.documentElement
    
    return {
      config: {
        appName: config.appName,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        faviconUrl: config.faviconUrl
      },
      applied: {
        title: document.title,
        primaryColor: root.style.getPropertyValue('--ion-color-primary'),
        secondaryColor: root.style.getPropertyValue('--ion-color-secondary'),
        faviconHref: document.querySelector('link[rel="icon"]')?.getAttribute('href')
      }
    }
  } catch (error) {
    console.warn('Error getting theme status:', error)
    return {
      config: {},
      applied: {},
      error: error
    }
  }
}

// Export for debugging in development - Subtask 4.4
if (import.meta.env.DEV) {
  (window as any).whitelabelTheme = {
    apply: applyWhitelabelTheme,
    validate: validateThemeApplication,
    status: getThemeStatus,
    updateFavicon: updateFaviconLink
  }
}